import "./styles.css";
import * as THREE from "three";
import cupsData from "./data/cups.json";
import type { Cup } from "./types";
import { createWorld } from "./scene";
import { placeCups } from "./layout";
import { attachInteraction } from "./interaction";
import { createHud } from "./hud";
import { createPhysics, initPhysics } from "./physics";
import { attachFocus } from "./focus";
import { loadCat } from "./cat";
import { createCatAI, defaultGoals, type CatAIHandle } from "./cat-ai";
import { buildPois } from "./pois";
import { createDebugStats } from "./debug-stats";
import { createPhysicsDebug } from "./physics-debug";
import { attachSelection } from "./wire-selection";
import { attachSearch } from "./wire-search";
import { attachCatControls } from "./wire-cat-controls";
import type { CupilotDevAPI } from "./dev-api";

// One-time WASM init for the physics SDK before anything that touches
// the world. Top-level await keeps the rest of setup straightforward.
await initPhysics();

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const world = createWorld(canvas);

const cups = cupsData as Cup[];
const cupMap = placeCups(world.cupRoot, world.shelf, cups);

const physics = createPhysics(world);
for (const { group } of cupMap.values()) {
  // Find the textured body mesh inside the cup group so the physics layer
  // can reuse its material on shards if the cup shatters.
  const bodyMesh = group.children.find(
    (c): c is THREE.Mesh =>
      c instanceof THREE.Mesh && c.userData.role === "body",
  );
  const outerMat =
    bodyMesh && !Array.isArray(bodyMesh.material)
      ? bodyMesh.material
      : undefined;
  physics.addCup(group, outerMat);
}

const hud = createHud();
hud.setCount(cups.length);

const interaction = attachInteraction(world, physics);
interaction.setCups(cupMap);
const focus = attachFocus(world, physics);

// Points of Interest (food + water bowl in front of the shelf). The
// cat AI uses these as drive-satisfying targets.
const poiHandle = buildPois();
world.scene.add(poiHandle.group);

// Cat handle is read live from this binding so the wiring helpers below
// see the latest value once the async GLB resolves.
let cat: Awaited<ReturnType<typeof loadCat>> = null;
let catAI: CatAIHandle | null = null;
const getCat = (): typeof cat => cat;

const { selectCup } = attachSelection({
  cupMap,
  hud,
  world,
  focus,
  interaction,
  getCat,
  poisGroup: poiHandle.group,
});

const search = attachSearch({ cups, cupMap, hud, world, selectCup });

// Debug mode gates the WASD cat-control. Tracked here so attachCatControls
// can read live state without re-wiring its keydown listeners every toggle.
let debugMode = false;
attachCatControls({
  canvas,
  world,
  focus,
  getCat,
  getDebugMode: () => debugMode,
});

// Async-load the cat. It walks back and forth in front of the shelf;
// fire-and-forget — the rest of the app works without it. Once the
// cat is up, attach the AI supervisor and re-apply the search
// spotlight if one's already running (otherwise the cat's late-
// arriving materials read at full brightness against a dimmed shelf).
loadCat(world, physics).then((c) => {
  cat = c;
  if (!c) return;
  hud.setCat(c);
  catAI = createCatAI({
    cat: c,
    pois: poiHandle.pois,
    onUpdate: world.onUpdate,
    goals: defaultGoals(),
  });
  search.reapplyForLateMaterials();
});

// Reset every cup to its original shelf position. Useful after the
// user has thrown most of the collection onto the floor. Disabled
// while focus mode is active — the focused cup is excluded from the
// simulation, so a reset would silently fail to move it (and the
// focus lerp would override it next frame anyway). Triggerable from
// the keyboard (R) or the Reset HUD button (the latter is the only
// path on phones / touch devices).
function resetCups(): void {
  if (focus.isActive()) return;
  physics.reset();
}
window.addEventListener("keydown", (e) => {
  if (e.key !== "r" && e.key !== "R") return;
  resetCups();
});
hud.onReset(resetCups);

hud.onLightModeChange((mode) => world.setLightMode(mode));

const debugStats = createDebugStats(world.renderer, physics);
const physicsDebug = createPhysicsDebug(world, physics);
hud.onDebugToggle((enabled) => {
  debugMode = enabled;
  debugStats.setVisible(enabled);
  physicsDebug.setVisible(enabled);
});

hud.setMaxLinvel(physics.getMaxLinvel());
hud.onMaxLinvelChange((v) => physics.setMaxLinvel(v));
world.onUpdate((dt) => debugStats.update(dt));

// Expose a few scene handles for headless debug scripts (focusing the
// camera on a specific cup, asserting physics invariants). Harmless in
// production but only used by scripts/debug/debug-page.mjs and friends.
// Shape lives in src/dev-api.ts — that's the source of truth for what
// scripts can rely on.
const devApi: CupilotDevAPI = {
  cupRoot: world.cupRoot,
  camera: world.camera,
  controls: world.controls,
  physics,
  focus,
  cupMap,
  scene: world,
  // The cat loads asynchronously, so these are read live by harness
  // scripts (e.g. scripts/debug/debug-cat-*.mjs) via the getters
  // rather than captured at this line.
  get cat() {
    return cat;
  },
  get catAI() {
    return catAI;
  },
  pois: poiHandle.pois,
};
window.__cupilot = devApi;
